import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Mail, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getQuiz, submitQuiz, trackEvent } from "@/lib/api";

const slide = { enter: { opacity: 0, y: 24 }, center: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -24 } };

const sessionId = (() => {
  const k = "ec_session";
  let v = localStorage.getItem(k);
  if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(k, v); }
  return v;
})();

export default function Quiz() {
  const [quiz, setQuiz] = useState(null);
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getQuiz().then(setQuiz).catch(() => toast.error("Couldn't load the quiz. Please refresh."));
    trackEvent("quiz_start", sessionId);
  }, []);

  const questions = quiz?.questions || [];
  const total = questions.length;
  const isContact = step === total;
  const progress = useMemo(() => {
    const answered = Object.keys(answers).length;
    return Math.round(((answered + (isContact ? 1 : 0)) / (total + 1)) * 100);
  }, [answers, total, isContact]);

  const selectOption = (q, opt) => {
    setAnswers((prev) => ({ ...prev, [q.id]: { option_id: opt.id, category: opt.category } }));
    setTimeout(() => setStep((s) => Math.min(s + 1, total)), 260);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error("What should we call you?");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Please enter a valid email.");
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(), email: email.trim(), session_id: sessionId,
        answers: questions.map((q) => ({ question_id: q.id, option_id: answers[q.id].option_id, category: answers[q.id].category })),
      };
      const data = await submitQuiz(payload);
      setResult(data);
      trackEvent("quiz_converted", sessionId, { result_category: data.result.category });
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!quiz) {
    return (
      <div className="min-h-screen bg-[#EAE6E1] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#64246A] animate-spin" data-testid="quiz-loading" />
      </div>
    );
  }

  if (result) return <Results data={result} />;

  // Intro screen
  if (!started) {
    return (
      <div className="min-h-screen bg-[#EAE6E1] text-[#2A1B2E] flex flex-col">
        <header className="flex items-center px-6 md:px-12 py-6 max-w-3xl mx-auto w-full">
          <Link to="/" className="font-serif text-lg" data-testid="quiz-brand-home">Empress Coaching</Link>
        </header>
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <motion.div variants={slide} initial="enter" animate="center" className="w-full max-w-2xl">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-6" data-testid="quiz-intro-title">
              {quiz.intro.title}
            </h1>
            {quiz.intro.body.map((p, i) => (
              <p key={i} className="text-lg text-[#2A1B2E]/75 leading-relaxed mb-3">{p}</p>
            ))}
            <button
              onClick={() => setStarted(true)}
              data-testid="quiz-intro-start-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#64246A] text-white rounded-md px-8 py-4 font-semibold hover:bg-[#4A1A50] transition-colors duration-300"
            >
              {quiz.intro.button} <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  const q = questions[step];

  return (
    <div className="min-h-screen bg-[#EAE6E1] text-[#2A1B2E] flex flex-col">
      <div className="fixed top-0 left-0 right-0 h-1.5 bg-[#2A1B2E]/10 z-20">
        <motion.div className="h-full bg-[#A87CA0]" initial={{ width: 0 }} animate={{ width: `${progress}%` }}
          transition={{ ease: "easeOut", duration: 0.5 }} data-testid="quiz-progress" />
      </div>

      <header className="flex items-center justify-between px-6 md:px-12 py-6 max-w-3xl mx-auto w-full">
        <Link to="/" className="font-serif text-lg" data-testid="quiz-brand-home">Empress Coaching</Link>
        {step > 0 && !isContact && (
          <button onClick={back} data-testid="quiz-back-btn"
            className="inline-flex items-center gap-1.5 text-sm text-[#6E5A70] hover:text-[#2A1B2E] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            {!isContact ? (
              <motion.div key={q.id} variants={slide} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#A87CA0] mb-3">
                  {step + 1} of {total}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl leading-tight tracking-tight mb-10">{q.prompt}</h2>
                <div className="grid gap-4">
                  {q.options.map((opt, i) => {
                    const selected = answers[q.id]?.option_id === opt.id;
                    return (
                      <motion.button key={opt.id} onClick={() => selectOption(q, opt)} data-testid={`quiz-option-${step}-${i}`}
                        whileHover={{ y: -3 }}
                        className={`text-left rounded-lg p-6 border transition-colors duration-300 flex items-center justify-between gap-4 ${
                          selected ? "bg-[#64246A] border-[#64246A] text-white" : "bg-white border-[#2A1B2E]/10 hover:border-[#A87CA0]"
                        }`}>
                        <span className="text-base sm:text-lg font-medium">{opt.label}</span>
                        <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center ${selected ? "border-white bg-white/20" : "border-[#2A1B2E]/25"}`}>
                          {selected && <Check className="w-4 h-4" />}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            ) : (
              <motion.div key="contact" variants={slide} initial="enter" animate="center" exit="exit"
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                <h2 className="font-serif text-3xl sm:text-4xl leading-tight tracking-tight mb-2">{quiz.capture.title}</h2>
                <p className="text-lg text-[#2A1B2E]/75 mb-8">{quiz.capture.subtitle}</p>
                <div className="grid gap-4">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name" data-testid="quiz-name-input"
                    className="w-full rounded-lg border border-[#2A1B2E]/15 bg-white px-5 py-4 text-lg focus:ring-2 focus:ring-[#64246A] focus:outline-none" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    placeholder="Email" type="email" data-testid="quiz-email-input"
                    className="w-full rounded-lg border border-[#2A1B2E]/15 bg-white px-5 py-4 text-lg focus:ring-2 focus:ring-[#64246A] focus:outline-none" />
                  <button onClick={handleSubmit} disabled={submitting} data-testid="quiz-submit-btn"
                    className="mt-2 inline-flex items-center justify-center gap-2 bg-[#64246A] text-white rounded-md px-8 py-4 font-semibold hover:bg-[#4A1A50] transition-colors duration-300 disabled:opacity-60">
                    {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> One moment…</>) : (<>Show me my results <ArrowRight className="w-4 h-4" /></>)}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

const CALENDLY = "https://calendly.com/jocelyn-empresscoachingandconsulting/one-on-one-30-min";

const RESULT_CONTENT = {
  possibility: {
    body: [
      `Nothing has to be wrong for you to want something different.`,
      `You may have gotten very good at being "realistic." You make sensible choices. You know how to make things work. Somewhere along the way, "What do I actually want?" may have turned into "What's realistic?" or even "Who am I to complain?"`,
      `Wanting more doesn't mean what you have isn't good enough.`,
      `You're still allowed to imagine.`,
    ],
    exercise: [
      `If you knew nobody would be disappointed, you couldn't fail, and you didn't have to explain yourself:`,
      `What would you want to be different a year from now?`,
      `Then ask:`,
      `What did I immediately try to talk myself out of?`,
      `That second answer may tell you as much as the first.`,
    ],
    coaching_context: false,
    rec_heading: `Ready to get clearer?`,
    rec_copy: [
      `The Road Map is a self-paced mini-course designed to help you get clear on what you actually want, why it matters, what could get in the way, and turn it into something concrete you can move toward.`,
    ],
    bullets: [
      `Get clear on what you actually want`,
      `Turn it into milestones and next steps`,
      `See what could get in the way before it stops you`,
    ],
    cta: { label: `Explore The Road Map`, to: `/road-map` },
  },
  self_trust: {
    body: [
      `You may not need more information. You may be looking for enough certainty to feel safe choosing.`,
      `Certainty that it's the right decision. That you won't regret it. That you aren't overlooking something important.`,
      `But the decisions that change our lives rarely come with guarantees.`,
      `At some point, the question shifts from:`,
      `"How do I know this is the right choice?"`,
      `to:`,
      `"Do I trust myself to make a choice and deal with what happens next?"`,
    ],
    exercise: [
      `Think about the decision you've been circling.`,
      `If I knew I couldn't make the wrong decision, I would ________.`,
      `The thing I'm most afraid will happen is ________.`,
      `If that happened, I would ________.`,
      `Pay attention to the third answer.`,
      `You may be more capable of handling the outcome than your fear has been giving you credit for.`,
    ],
    coaching_context: true,
    rec_heading: `Want another set of eyes on it?`,
    rec_copy: [
      `Sometimes another person can hear what's underneath all the reasons faster than we can hear it ourselves.`,
      `Bring one decision, challenge, or place you feel stuck.`,
    ],
    bullets: [
      `Get a fresh perspective on what may be getting in the way`,
      `Look at the decision without pressure to already know the answer`,
      `Leave with a clearer next step, whether or not we work together`,
    ],
    cta: { label: `Book a free 30-minute discovery call`, href: CALENDLY },
  },
  momentum: {
    body: [
      `You've started before. You've made the plan and meant it.`,
      `Then doubt shows up. Life gets complicated. The work gets uncomfortable. Distraction becomes easier than continuing.`,
      `The problem may not be starting. The harder part may be what happens inside you when continuing feels uncomfortable, uncertain, or unfamiliar.`,
    ],
    exercise: [
      `Think about the last thing you really intended to follow through on but didn't.`,
      `Instead of asking:`,
      `"Why can't I keep going?"`,
      `ask:`,
      `"What was happening right before I stopped?"`,
      `Did it get uncomfortable? Harder? Did something suddenly feel more urgent? Did doubt creep in?`,
      `Now finish this sentence:`,
      `When ________ happens, I tend to ________.`,
      `That's the pattern to pay attention to.`,
    ],
    coaching_context: true,
    rec_heading: `Want help seeing what's stopping you?`,
    rec_copy: [
      `If you already know what you want but keep finding yourself back in the same place, another plan probably isn't the answer.`,
      `Bring one decision, challenge, or place you feel stuck.`,
    ],
    bullets: [
      `Get a fresh perspective on what may be getting in the way`,
      `Look at what tends to happen right before you lose momentum`,
      `Leave with a clearer next step, whether or not we work together`,
    ],
    cta: { label: `Book a free 30-minute discovery call`, href: CALENDLY },
  },
  capacity: {
    body: [
      `Seriously.`,
      `You already have a lot that matters. Work. Family. Responsibilities. People who need you. Things you've committed to and things you genuinely want to be doing.`,
      `So "make yourself a priority" isn't very useful advice when you're wondering where, exactly, you're supposed to put that priority.`,
      `The issue may not be motivation. You may simply need more room.`,
    ],
    exercise: [
      `Think of the one thing taking up the most room in your life right now.`,
      `Ask:`,
      `Does this need to be done?`,
      `Does it need to be done this way?`,
      `Does it need to be done by me?`,
      `Does it need to be done now?`,
      `Look for one answer that surprises you.`,
      `One thing you could stop, postpone, simplify, delegate, ask for help with, or do differently.`,
      `That's room.`,
    ],
    coaching_context: true,
    rec_heading: `Want another set of eyes on it?`,
    rec_copy: [
      `When you're carrying everything, it can be hard to see what could actually change.`,
      `Bring one decision, challenge, or place you feel stuck.`,
    ],
    bullets: [
      `Get a fresh perspective on what may be taking up unnecessary room`,
      `Look for one place something could become lighter`,
      `Leave with a clearer next step, whether or not we work together`,
    ],
    cta: { label: `Book a free 30-minute discovery call`, href: CALENDLY },
  },
};

function Results({ data }) {
  const r = data.result;
  const c = RESULT_CONTENT[r.category] || RESULT_CONTENT.possibility;
  return (
    <div className="min-h-screen bg-[#EAE6E1] text-[#2A1B2E]">
      <section className="bg-[#64246A] text-[#EAE6E1]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-sm uppercase tracking-[0.25em] font-semibold text-[#EAE6E1]/70 mb-5">
            For {data.name}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl leading-[1.08] tracking-tight" data-testid="result-headline">
            {r.headline}
          </motion.h1>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-14" data-testid="result-body">
        {c.body.map((p, i) => (
          <p key={i} className="text-lg sm:text-xl leading-relaxed text-[#2A1B2E]/85 mb-4">{p}</p>
        ))}
        {data.email_sent && (
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-[#6E5A70]"><Mail className="w-4 h-4" /> We've sent a copy to your inbox.</p>
        )}
      </section>

      {/* Exercise */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-14">
        <div className="rounded-lg bg-white border border-[#2A1B2E]/10 p-8 md:p-10" data-testid="result-exercise">
          <h2 className="font-serif text-2xl sm:text-3xl mb-6">Try this right now</h2>
          {c.exercise.map((line, i) => (
            <p key={i} className="text-base sm:text-lg leading-relaxed text-[#2A1B2E]/80 mb-3">{line}</p>
          ))}
        </div>
      </section>

      {/* Coaching context (SELF-TRUST, MOMENTUM, CAPACITY only) */}
      {c.coaching_context && (
        <section className="max-w-3xl mx-auto px-6 md:px-12 pb-14" data-testid="result-coaching-context">
          <div className="rounded-lg bg-[#64246A]/5 border border-[#2A1B2E]/10 p-6 md:p-8">
            <h3 className="font-serif text-xl mb-3">What coaching with me is like</h3>
            <p className="text-base text-[#2A1B2E]/80 leading-relaxed">
              My coaching is warm, direct, and practical. I won't tell you what you should do. I'll help you see what may be hard to see from inside your own thinking and get clearer about what you want to do next.
            </p>
          </div>
        </section>
      )}

      {/* Single recommendation + one primary CTA */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-16" data-testid="result-recommendation">
        <h2 className="font-serif text-2xl sm:text-3xl mb-4">{c.rec_heading}</h2>
        {c.rec_copy.map((p, i) => (
          <p key={i} className="text-lg text-[#2A1B2E]/80 leading-relaxed mb-3">{p}</p>
        ))}
        <ul className="mt-4 space-y-2.5 mb-7">
          {c.bullets.map((b, i) => (
            <li key={i} className="flex gap-2.5 text-base text-[#2A1B2E]/80"><Check className="w-4 h-4 text-[#A87CA0] shrink-0 mt-1" /><span>{b}</span></li>
          ))}
        </ul>
        {c.cta.to ? (
          <Link to={c.cta.to} data-testid="result-primary-cta"
            className="inline-flex items-center gap-2 rounded-md px-8 py-4 font-semibold bg-[#64246A] text-white hover:bg-[#4A1A50] transition-colors duration-300">
            {c.cta.label} <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <a href={c.cta.href} target="_blank" rel="noopener noreferrer" data-testid="result-primary-cta"
            className="inline-flex items-center gap-2 rounded-md px-8 py-4 font-semibold bg-[#64246A] text-white hover:bg-[#4A1A50] transition-colors duration-300">
            {c.cta.label} <ArrowRight className="w-4 h-4" />
          </a>
        )}
      </section>

      {/* Quiet link + retake */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-16 border-t border-[#2A1B2E]/10 pt-10 text-center space-y-4">
        <a href="/#ways-to-work" data-testid="result-ways-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#64246A] hover:opacity-80 transition-opacity">
          See all ways to work with me →
        </a>
        <div>
          <Link to="/quiz" onClick={() => window.location.reload()} data-testid="retake-quiz-btn"
            className="inline-flex items-center gap-2 text-sm text-[#6E5A70] hover:text-[#2A1B2E] transition-colors">
            <RefreshCw className="w-4 h-4" /> Retake the quiz
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#2A1B2E]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 text-center text-sm text-[#2A1B2E]/60 font-serif">
          Lead from within. Create what matters. · Empress Coaching &amp; Consulting
        </div>
      </footer>
    </div>
  );
}
